import {
  SetStateAction,
  useCallback,
  useState,
  Dispatch,
  useEffect,
  useRef,
  useMemo,
} from "react";
import SortableTree, { TreeItem } from "react-sortable-tree";

// Assets
import "react-sortable-tree/style.css";

// API related
import { IDirectoryDepartment } from "#src/services/endpoint/directory/types";
import directoryService from "#src/services/endpoint/directory";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";

// Children
import { DepartmentNode } from "./DepartmentNode";
import DeleteDepartmentModal from "./DeleteDepartmentModal";
import { createToast } from "#src/common/system/toasts";
import { useTranslation } from "react-i18next";

type TreeItemWithId = TreeItem & {
  id: string;
  children?: TreeItemWithId[];
};

type Props = {
  workspaceId: string;
  workspaceName: string;
  departments: IDirectoryDepartment[];
  setDepartments: Dispatch<SetStateAction<IDirectoryDepartment[]>>;
};

export const DepartmentDiagram = (props: Props) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "directory.departments",
  });

  const { departments, setDepartments, workspaceName, workspaceId } = props;

  // --------------- DATA ---------------

  const [treeData, setTreeData] = useState<TreeItemWithId[]>([]);

  // Modals
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    string | null
  >(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Only build tree once per workspace
  const currentWorkspaceId = useRef<string | null>(null);

  // --------------- COMPUTED ---------------

  const selectedDepartment = useMemo(
    () => departments.find((item) => item.id === selectedDepartmentId),
    [departments, selectedDepartmentId]
  );

  // --------------- METHODS ---------------

  // Edit node (name)
  const handleEdit = useCallback(
    (id: string, newName: string) => {
      // Update departments
      setDepartments((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                name: newName,
                parent_id: item.parent_id === "0" ? null : item.parent_id, // Ignore parent_id if it's '0'
              }
            : item
        )
      );

      // No need to update the treeData here, as its state is managed by the DepartmentNode component

      // Send API
      directoryService
        .editDepartment(workspaceId, id, {
          name: newName,
        })
        .then(() => {
          createToast({
            type: "success",
            message: t("notification.renameDepartment.success.message"),
            detail: t("notification.renameDepartment.success.detail", {
              name: newName,
            }),
          });
        })
        .catch((error) => {
          apiErrorHandler(error);
        });
    },
    [setDepartments, workspaceId, t]
  );

  // Add new node
  const handleAddChild = useCallback(
    async (parentId: string) => {
      let newChild: IDirectoryDepartment;

      // Send API
      try {
        newChild = await directoryService.createDepartment(workspaceId, {
          name: "New Department",
          parent_id: parentId === "0" ? null : parentId,
        });
      } catch (error) {
        apiErrorHandler(error);
        return;
      }

      createToast({
        type: "success",
        message: t("notification.createDepartment.success.message"),
      });

      // Update departments
      setDepartments((prev) => [...prev, newChild]);

      // Expand the parent node and add the new child to it
      setTreeData((prevTreeData) => {
        const addChildToTree = (nodes: TreeItemWithId[]): TreeItemWithId[] => {
          return nodes.map((node) => {
            if (node.id === parentId) {
              return {
                ...node,
                expanded: true, // Expand the parent node
                children: [
                  ...(node.children || []),
                  {
                    id: newChild.id,
                    title: (
                      <DepartmentNode
                        id={newChild.id}
                        name={newChild.name}
                        onAddChild={handleAddChild}
                        onDelete={() => {
                          setSelectedDepartmentId(newChild.id);
                          setIsDeleteModalOpen(true);
                        }}
                        onEdit={handleEdit}
                      />
                    ),
                    children: [],
                  },
                ],
              };
            }

            return {
              ...node,
              children: node.children ? addChildToTree(node.children) : [],
            };
          });
        };

        return addChildToTree(prevTreeData);
      });

      // Focus and enter edit mode for the newly added node
      let attempts = 0;
      const maxAttempts = 3;

      const tryFocusNewNode = () => {
        const newNodeElement = document.getElementById(
          `department-node-${newChild.id}`
        );
        if (newNodeElement) {
          const editButton: HTMLButtonElement | null =
            newNodeElement.querySelector(".tree-node__title");
          if (editButton) {
            editButton.click(); // Trigger the edit button to enter edit mode
            return;
          }
        }

        // Retry if the button is not found or not clickable
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(tryFocusNewNode, 100); // Retry after 100ms
        }
      };

      tryFocusNewNode();
    },
    [handleEdit, setDepartments, workspaceId, t]
  );

  // Delete node
  const handleDelete = useCallback(
    (id: string) => {
      // Update departments
      setDepartments((prev) => {
        // Helper function to get all descendant IDs of a node
        const getDescendantIds = (
          nodeId: string,
          items: IDirectoryDepartment[]
        ): string[] => {
          const children = items.filter((item) => item.parent_id === nodeId);
          return children.reduce(
            (acc, child) => [
              ...acc,
              child.id,
              ...getDescendantIds(child.id, items),
            ],
            [] as string[]
          );
        };

        // Get all IDs to delete (the node itself and all its descendants)
        const idsToDelete = [id, ...getDescendantIds(id, prev)];

        // Filter out all nodes with IDs in the idsToDelete array
        return prev.filter((item) => !idsToDelete.includes(item.id));
      });

      // Remove node and all of its descendants from the tree
      setTreeData((prevTreeData) => {
        // Recursive function to filter out the node and its descendants
        const filterTree = (nodes: TreeItemWithId[]): TreeItemWithId[] => {
          return nodes
            .filter((node) => node.id !== id) // Remove the node itself
            .map((node) => ({
              ...node,
              children: node.children ? filterTree(node.children) : [], // Recursively filter children
            }));
        };

        return filterTree(prevTreeData);
      });
    },
    [setDepartments]
  );

  // Build tree data from departments
  const buildTree = useCallback(
    (items: IDirectoryDepartment[]): TreeItemWithId[] => {
      const map: { [key: string]: TreeItemWithId } = {};
      const tree: TreeItemWithId[] = [];

      // Add a virtual root node
      const rootNode: IDirectoryDepartment = {
        id: "0",
        name: workspaceName,
        parent_id: null,
        created_time: 0,
      };

      const allItems = [
        rootNode,
        ...items.map((item) => ({
          ...item,
          parent_id: item.parent_id || "0",
        })),
      ];

      // Build the map of nodes
      allItems.forEach((item) => {
        map[item.id] = {
          children: [],
          id: item.id,
          expanded: item.id === "0", // Always expand root node
          title: (
            <DepartmentNode
              id={item.id}
              name={item.name}
              onAddChild={handleAddChild}
              onDelete={() => {
                setSelectedDepartmentId(item.id);
                setIsDeleteModalOpen(true);
              }}
              onEdit={handleEdit}
            />
          ),
        };
      });

      // Build the tree structure
      allItems.forEach((item) => {
        if (!item.parent_id) {
          tree.push(map[item.id]);
        } else {
          map[item.parent_id]?.children?.push(map[item.id]);
        }
      });

      // Set expanded for the first 2 levels
      const setExpandedForLevels = (node: TreeItemWithId, depth: number) => {
        if (depth <= 2) {
          node.expanded = true; // Expand the first two levels
        }
        node.children?.forEach((child: TreeItemWithId) =>
          setExpandedForLevels(child, depth + 1)
        );
      };
      tree.forEach((node) => setExpandedForLevels(node, 1));

      return tree;
    },
    [handleAddChild, handleEdit, workspaceName]
  );

  // --------------- EFFECTS ---------------

  useEffect(() => {
    // Build the tree data only once per workspace
    if (workspaceId && currentWorkspaceId.current !== workspaceId) {
      currentWorkspaceId.current = workspaceId;
      setTreeData(buildTree(departments));
    }
  }, [buildTree, departments, workspaceId]);

  // --------------- RENDER ---------------

  return (
    <>
      <SortableTree
        className="department-diagram v2"
        treeData={treeData}
        getNodeKey={({ node }) => node.id}
        onChange={setTreeData}
        isVirtualized={false}
        onMoveNode={({ node, nextParentNode, nextPath, prevPath }) => {
          // Only update the departments state if the parent node has changed
          if (nextPath.join("/") !== prevPath.join("/")) {
            // Ignore parent_id if new parent is root
            const newParentId =
              nextParentNode && nextParentNode.id !== "0"
                ? nextParentNode.id
                : null;

            // Update diagram
            setDepartments((prev) =>
              prev.map((item) =>
                item.id === node.id
                  ? {
                      ...item,
                      parent_id: newParentId,
                    }
                  : item
              )
            );

            // Send API
            directoryService
              .editDepartment(workspaceId, node.id, {
                parent_id: newParentId,
              })
              .then(() => {
                createToast({
                  type: "success",
                  message: t("notification.moveDepartment.success.message"),
                });
              })
              .catch((error) => {
                apiErrorHandler(error);
              });
          }
        }}
        canDrag={({ node }) => (node as TreeItemWithId).id !== "0"}
        canDrop={({ nextParent }) => !!nextParent}
      />

      {/* Modals */}
      <DeleteDepartmentModal
        isOpen={isDeleteModalOpen}
        workspaceId={workspaceId}
        departmentId={selectedDepartmentId || ""}
        departmentName={selectedDepartment?.name || ""}
        closeModal={() => {
          setIsDeleteModalOpen(false);
          setSelectedDepartmentId(null);
        }}
        onSuccess={handleDelete}
      />
      {/* Modals end */}
    </>
  );
};
